'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MaskedPhoneProps } from './types';

import IconDotsLoader from '@/assets/icons/IconDotsLoader.svg';
import sendPhoneClick, {
  getCurrentTimestampWithTimezone,
} from '@/shared/api/sendPhoneClick';
import { classNames } from '@/shared/lib/classNames/classNames';
import { getBrowserName } from '@/shared/utils/browserUtils';

export const MaskedPhone = ({
  phoneNumber,
  IconStart,
  message,
  loadingMessage,
  containerClassName,
  phoneNumberClassName,
  phoneNumberMaskClassName,
  messageClassName,
  iconDotsClassName,
}: MaskedPhoneProps) => {
  const [isPhoneRevealed, setIsPhoneRevealed] = useState(false);
  const [isMangoReady, setIsMangoReady] = useState(false);
  const [isWaitingForMango, setIsWaitingForMango] = useState(false);

  const phoneElementRef = useRef<HTMLAnchorElement | null>(null);

  // Подписываемся на событие MangoEmtReplaced для отслеживания статуса трансформации
  useEffect(() => {
    const handleMangoReplaced = () => {
      setIsMangoReady(true);
    };

    window.addEventListener('MangoEmtReplaced', handleMangoReplaced);

    // Таймаут на случай если событие не придёт (5 секунд)
    const timeout = setTimeout(handleMangoReplaced, 5000);

    return () => {
      window.removeEventListener('MangoEmtReplaced', handleMangoReplaced);
      clearTimeout(timeout);
    };
  }, []);

  const revealPhoneAndSendTracking = useCallback(() => {
    if (!phoneElementRef.current) {
      return;
    }

    setIsPhoneRevealed(true);
    setIsWaitingForMango(false);

    // Получаем трансформированный номер из href
    const href = phoneElementRef.current.getAttribute('href') || '';
    const cleanedPhoneNumber = href.replace('tel:', '').replace(/[\s-]/g, '');

    // Отправляем трекинг
    sendPhoneClick({
      phone_number: cleanedPhoneNumber,
      browser: getBrowserName(),
      timestamp: getCurrentTimestampWithTimezone(),
    });
  }, []);

  // Когда Mango готов и мы ждали клика - показываем номер и отправляем трекинг
  useEffect(() => {
    if (isMangoReady && isWaitingForMango) {
      revealPhoneAndSendTracking();
    }
  }, [isMangoReady, isWaitingForMango, revealPhoneAndSendTracking]);

  const handlePhoneClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Если уже раскрыт - пускаем звонить
    if (isPhoneRevealed) {
      return;
    }

    // Первый клик блокируем (клик должен быть на маску)
    e.preventDefault();
  };

  const handleMaskClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Если Mango уже готов - сразу показываем и отправляем
    if (isMangoReady) {
      revealPhoneAndSendTracking();
    } else {
      // Mango еще не готов - ставим флаг ожидания (useEffect обработает)
      setIsWaitingForMango(true);
    }
  };

  return (
    <div
      className={
        containerClassName ??
        'md:bg-surface-base-tertiary max-sm:bg-surface-base-secondary flex w-full max-w-[430px] flex-col rounded-[20px] max-md:pb-6 max-sm:p-5 md:px-6 md:py-5'
      }
    >
      <div className="relative flex items-center text-[20px] leading-[32px]">
        {IconStart}
        {/* Link всегда в DOM (невидимый), Mango трансформирует его */}
        <Link
          ref={phoneElementRef}
          href={`tel:${phoneNumber.replace(/[\s-]/g, '')}`}
          className={classNames({
            cls: 'text-nowrap transition-opacity duration-300',
            mods: {
              'opacity-100': isPhoneRevealed,
              'pointer-events-none absolute opacity-0': !isPhoneRevealed,
              ...(phoneNumberClassName ? { [phoneNumberClassName]: true } : {}),
              ['text-[20px] leading-[32px]']: !phoneNumberClassName,
            },
          })}
          onClick={handlePhoneClick}
        >
          <span suppressHydrationWarning={true}>{phoneNumber}</span>
        </Link>

        {/* Маска поверх, показывается пока не кликнули */}
        {!isPhoneRevealed && !isWaitingForMango && (
          <div
            className={classNames({
              cls: 'flex cursor-pointer items-center text-nowrap',
              mods: {
                ...(phoneNumberMaskClassName
                  ? { [phoneNumberMaskClassName]: true }
                  : {}),
                ['text-[20px] leading-[32px]']: !phoneNumberMaskClassName,
                ['left-9']: !!IconStart,
              },
            })}
            onClick={handleMaskClick}
          >
            8 800 ***-**-**
          </div>
        )}

        {/* Loader пока ждем Mango */}
        {!isPhoneRevealed && isWaitingForMango && (
          <div
            className={classNames({
              cls: 'flex flex-nowrap items-center',
              mods: {
                ['left-9']: !!IconStart,
              },
            })}
          >
            <div
              className={classNames({
                cls: 'flex items-center gap-2 text-nowrap',
                mods: {
                  ...(phoneNumberMaskClassName
                    ? { [phoneNumberMaskClassName]: true }
                    : {}),
                  ['text-text-base-secondary text-[20px] leading-[32px]']:
                    !phoneNumberMaskClassName,
                },
              })}
            >
              <span>{loadingMessage}</span>
              <IconDotsLoader
                className={classNames({
                  cls: 'text-nowrap opacity-50',
                  mods: {
                    ...(iconDotsClassName ? { [iconDotsClassName]: true } : {}),
                    ['h-6 w-6']: !iconDotsClassName,
                  },
                })}
              />
            </div>
          </div>
        )}
      </div>
      {message && (
        <span
          className={
            messageClassName ??
            'text-text-base-secondary mt-2 text-[14px] leading-[20px]'
          }
        >
          {message}
        </span>
      )}
    </div>
  );
};
